import { useCallback, useRef, useState, type CSSProperties, type ChangeEvent, type DragEvent } from "react";
import { CanvasEditor, type CanvasEditorHandle } from "./components/CanvasEditor";
import {
  DownloadIcon,
  LockIcon,
  ProfileIcon,
  RefreshIcon,
  ShareIcon,
  StoryIcon,
  UploadIcon,
} from "./components/Icons";
import { campaign } from "./config/campaign";
import type { LayoutKey, Transform } from "./lib/geometry";

const initialTransform: Transform = { zoom: 1, panX: 0, panY: 0 };

type Toast = { kind: "success" | "error"; message: string } | null;

function App() {
  const inputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<CanvasEditorHandle>(null);
  const [layout, setLayout] = useState<LayoutKey>("story");
  const [photo, setPhoto] = useState<HTMLImageElement | null>(null);
  const [transforms, setTransforms] = useState<Record<LayoutKey, Transform>>({
    story: { ...initialTransform },
    avatar: { ...initialTransform },
  });
  const [busy, setBusy] = useState<"download" | "share" | null>(null);
  const [completed, setCompleted] = useState(false);
  const [toast, setToast] = useState<Toast>(null);

  const showToast = useCallback((next: Toast) => {
    setToast(next);
    window.setTimeout(() => setToast(null), 3200);
  }, []);

  const handleFrameError = useCallback(() => {
    showToast({ kind: "error", message: "A moldura não carregou. Atualize a página e tente novamente." });
  }, [showToast]);

  const choosePhoto = useCallback(() => inputRef.current?.click(), []);

  function loadFile(file?: File) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showToast({ kind: "error", message: "Esse arquivo não é uma imagem. Escolha um JPG, PNG ou WEBP." });
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      showToast({ kind: "error", message: "A foto tem mais de 25 MB. Escolha uma imagem menor." });
      return;
    }

    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      setPhoto(image);
      setTransforms({ story: { ...initialTransform }, avatar: { ...initialTransform } });
      setCompleted(false);
      URL.revokeObjectURL(url);
      showToast({ kind: "success", message: "Foto pronta. Agora ajuste o enquadramento." });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      showToast({ kind: "error", message: "Não conseguimos abrir essa foto. Tente outra imagem." });
    };
    image.src = url;
  }

  function onFileChange(event: ChangeEvent<HTMLInputElement>) {
    loadFile(event.target.files?.[0]);
    event.target.value = "";
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    loadFile(event.dataTransfer.files?.[0]);
  }

  async function makeFile() {
    if (!photo) throw new Error("Escolha uma foto antes de gerar o santinho.");
    const blob = await editorRef.current?.exportBlob();
    if (!blob) throw new Error("Não foi possível gerar a imagem.");
    return new File([blob], `santinho-${campaign.slug}-${layout}.png`, { type: "image/png" });
  }

  async function download() {
    setBusy("download");
    try {
      const file = await makeFile();
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      setCompleted(true);
      showToast({ kind: "success", message: "Santinho baixado. Agora é só compartilhar!" });
    } catch (error) {
      showToast({ kind: "error", message: error instanceof Error ? error.message : "Não foi possível baixar." });
    } finally {
      setBusy(null);
    }
  }

  async function share() {
    setBusy("share");
    try {
      const file = await makeFile();
      const shareData: ShareData = {
        title: `Meu Santinho com ${campaign.shortName}`,
        text: campaign.shareText,
        url: window.location.href,
      };

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ ...shareData, files: [file] });
      } else if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(`${campaign.shareText} ${window.location.href}`);
        showToast({ kind: "success", message: "Texto e link copiados para você compartilhar." });
      }
      setCompleted(true);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      showToast({ kind: "error", message: "O compartilhamento não abriu. Baixe a imagem e envie pelo WhatsApp." });
    } finally {
      setBusy(null);
    }
  }

  const step = completed ? 3 : photo ? 2 : 1;
  const transform = transforms[layout];
  const displayNumber = `${campaign.number.slice(0, 2)}.${campaign.number.slice(2)}`;
  const campaignTheme = {
    "--blue-950": campaign.theme.deep,
    "--blue-900": campaign.theme.surface,
    "--blue-650": campaign.theme.action,
    "--yellow": campaign.theme.accent,
    "--green": campaign.theme.success,
    "--ink": campaign.theme.ink,
    "--muted": campaign.theme.muted,
    "--surface": campaign.theme.surface,
    "--surface-raised": campaign.theme.surfaceRaised,
  } as CSSProperties;

  return (
    <main className="app-shell" style={campaignTheme}>
      <section className="campaign-panel" aria-label="Campanha">
        <div className="halftone halftone--one" />
        <div className="halftone halftone--two" />
        <div className="candidate-lockup">
          <div className="party-mark" aria-label={`Partido ${campaign.party}`}>
            <strong>{campaign.party}</strong>
            <small>PARTIDO LIBERAL</small>
          </div>
          <div className="candidate-copy">
            <p className="candidate-name">{campaign.candidate}</p>
            <p className="candidate-office">{campaign.office}</p>
          </div>
        </div>
        <div className="campaign-message">
          <p className="eyebrow">{campaign.slogan}</p>
          <h1>{displayNumber}</h1>
          <p>{campaign.supportingText}</p>
        </div>
        <div className="campaign-swoosh" aria-hidden="true"><span /><span /></div>
      </section>

      <section className="studio-panel" aria-label="Editor de santinho">
        <header className="mobile-lockup">
          <div className="party-mark party-mark--compact" aria-hidden="true">
            <strong>{campaign.party}</strong>
          </div>
          <div>
            <span>{campaign.shortName}</span>
            <strong>{displayNumber}</strong>
          </div>
          <div className="brand-kicker" aria-label="Eu voto">
            <b>EU</b><em>VOTO!</em>
          </div>
        </header>

        <ol className="progress" aria-label={`Etapa ${step} de 3`}>
          {["Foto", "Ajuste", "Compartilhe"].map((label, index) => {
            const number = index + 1;
            return (
              <li key={label} className={number === step ? "active" : number < step ? "done" : ""}>
                <span>{number < step ? "✓" : number}</span>{label}
              </li>
            );
          })}
        </ol>

        <div className="layout-switcher" role="tablist" aria-label="Formato da imagem">
          <button
            type="button"
            role="tab"
            aria-selected={layout === "story"}
            className={layout === "story" ? "active" : ""}
            onClick={() => setLayout("story")}
          ><StoryIcon /> Story</button>
          <button
            type="button"
            role="tab"
            aria-selected={layout === "avatar"}
            className={layout === "avatar" ? "active" : ""}
            onClick={() => setLayout("avatar")}
          ><ProfileIcon /> Perfil</button>
        </div>

        <div className="editor-zone" onDragOver={(event) => event.preventDefault()} onDrop={onDrop}>
          <CanvasEditor
            ref={editorRef}
            layout={layout}
            image={photo}
            onChoose={choosePhoto}
            transform={transform}
            onTransformChange={(next) => setTransforms((current) => ({ ...current, [layout]: next }))}
            onFrameError={handleFrameError}
          />
        </div>

        <div className="adjustments">
          <label htmlFor="zoom">Zoom</label>
          <input
            id="zoom"
            type="range"
            min="1"
            max="3"
            step="0.01"
            value={transform.zoom}
            disabled={!photo}
            onChange={(event) => setTransforms((current) => ({
              ...current,
              [layout]: { ...current[layout], zoom: Number(event.target.value) },
            }))}
          />
          <span>{Math.round(transform.zoom * 100)}%</span>
          <p>{photo ? "Arraste ou faça pinça para ajustar" : "Escolha uma foto para começar"}</p>
        </div>

        <div className="action-area">
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFileChange} hidden />
          {!photo ? (
            <button className="primary-action" type="button" onClick={choosePhoto}>
              <UploadIcon /> Escolher foto
            </button>
          ) : (
            <div className="ready-actions">
              <button className="icon-action" type="button" onClick={choosePhoto} aria-label="Trocar foto" title="Trocar foto">
                <RefreshIcon />
              </button>
              <button className="primary-action" type="button" onClick={download} disabled={busy !== null}>
                <DownloadIcon /> {busy === "download" ? "Gerando imagem..." : "Baixar imagem"}
              </button>
              <button className="icon-action" type="button" onClick={share} disabled={busy !== null} aria-label="Compartilhar santinho" title="Compartilhar santinho">
                <ShareIcon />
              </button>
            </div>
          )}
          <p className="privacy"><LockIcon /> Sua foto fica só neste aparelho</p>
          {campaign.election && (
            <footer className="campaign-disclosure" aria-label="Identificação oficial da campanha eleitoral">
              <p>
                <span className="campaign-copy-line">
                  <strong>{campaign.shortName} © {campaign.election.year}</strong>
                </span>
                <span className="campaign-copy-line">
                  Candidato a {campaign.office} por {campaign.election.state}
                </span>
                <span className="campaign-copy-line">
                  {campaign.party} Nº {campaign.number}.
                </span>
              </p>
              <p>
                <span className="campaign-copy-line">
                  <span className="campaign-ad-label">Propaganda Eleitoral</span> · Eleição {campaign.election.year}
                </span>
                <span className="campaign-copy-line">
                  {campaign.election.legalName} {campaign.office}
                </span>
                <span className="campaign-copy-line">
                  CNPJ do comitê financeiro: <span className="campaign-cnpj">{campaign.election.campaignCnpj}</span>.
                </span>
              </p>
            </footer>
          )}
        </div>
      </section>

      <div className={`toast ${toast ? "toast--visible" : ""} ${toast?.kind === "error" ? "toast--error" : ""}`} role="status" aria-live="polite">
        {toast?.message}
      </div>
    </main>
  );
}

export default App;
